import { useState } from "react";
import { Text, View } from "react-native";
import { Check, Copy } from "lucide-react-native";
import {
  Dialog,
  DialogActions,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  colors,
  typography,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";

const SHARE_DIALOG_ILLUSTRATION =
  require("@src/apps/moon/assets/images/share-dialog-illustration-v3.png") as number;

type Props = {
  visible: boolean;
  url: string;
  /** Fired when the user copies the link. */
  onChannelSelect?: (channel: "copy") => void;
  onClose: () => void;
};

/**
 * Web fallback for the invite flow.
 *
 * Native iOS/Android callers open the platform share sheet directly from
 * InviteFriend. This component is the web fallback: one canonical dialog,
 * one link, and one copy action.
 */
export default function ShareSheet({
  visible,
  url,
  onChannelSelect,
  onClose,
}: Props) {
  const t = useTranslations();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    onChannelSelect?.("copy");
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      testID="share-louise-dialog"
    >
      <DialogIcon
        source={SHARE_DIALOG_ILLUSTRATION}
        width={80}
        height={80}
        style={{ marginBottom: 16 }}
      />

      <DialogHeader
        title={t("you.invite_friend.share_sheet_title")}
        subtitle={t("you.invite_friend.share_sheet_subtitle")}
        style={{ alignItems: "center", width: "100%", marginBottom: 20 }}
        titleStyle={{ textAlign: "center" }}
        subtitleStyle={{ textAlign: "center", width: "100%" }}
      />

      <View style={styles.urlBox}>
        <Text
          selectable
          numberOfLines={2}
          ellipsizeMode="middle"
          style={styles.urlText}
        >
          {url}
        </Text>
      </View>

      <DialogActions style={styles.actions}>
        <DialogPrimaryButton
          label={
            copied
              ? t("you.invite_friend.share_sheet_copied")
              : t("you.invite_friend.share_sheet_copy")
          }
          onPress={() => void handleCopy()}
          leading={
            copied ? (
              <Check size={17} color={colors.surface} strokeWidth={2.2} />
            ) : (
              <Copy size={17} color={colors.surface} strokeWidth={1.8} />
            )
          }
        />
      </DialogActions>
    </Dialog>
  );
}

const styles = {
  urlBox: {
    minHeight: 56,
    justifyContent: "center" as const,
    paddingHorizontal: 14,
    paddingVertical: 11,
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.58)",
    borderWidth: 1,
    borderColor: "rgba(42,37,34,0.10)",
  },
  urlText: {
    fontFamily: typography.families.mono,
    fontSize: 12,
    lineHeight: 17,
    color: colors.text,
  },
  actions: {
    flexDirection: "column" as const,
    gap: 10,
    marginTop: 20,
  },
};
